import { woodPanel } from '@/shared/ui/game-theme';
import { copperButton, gameColors, sectionHeading } from '@/shared/ui/game-theme';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useT } from '@/i18n';
import { useDraggableJournal } from '../hooks/use-draggable-journal';

const DEFAULT_MAX_VISIBLE_LINES = 5;

const panelBase: CSSProperties = {
  position: 'absolute',
  bottom: '26%',
  zIndex: 50,
  maxWidth: 200,
  padding: '8px 10px',
  borderRadius: 8,
  backgroundColor: 'rgba(8, 10, 18, 0.82)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
  pointerEvents: 'auto',
  ...woodPanel,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 4,
};

const titleStyle: CSSProperties = {
  ...sectionHeading,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'none',
  color: gameColors.muted,
};

const openLogButtonStyle: CSSProperties = {
  ...copperButton,
  width: 28,
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  borderRadius: 5,
  border: '1px solid rgba(255,255,255,0.16)',
  background: 'rgba(255,255,255,0.08)',
  color: gameColors.text,
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
  padding: 0,
};

const headerActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
};

const scrollStyle: CSSProperties = {
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  fontSize: 12,
  lineHeight: 1.45,
  color: gameColors.text,
  scrollbarWidth: 'thin',
};

const lineStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  wordBreak: 'break-word',
};

interface CombatLogPanelProps {
  lines: string[];
  /** Dock panel on this side; floating damage stays near center / opposite side. */
  anchor: 'left' | 'right';
  visible: boolean;
  title?: string;
  maxVisibleLines?: number;
  onOpenLog?: () => void;
}

export function CombatLogPanel({
  lines,
  anchor,
  visible,
  title,
  maxVisibleLines = DEFAULT_MAX_VISIBLE_LINES,
  onOpenLog,
}: CombatLogPanelProps) {
  const tr = useT();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [minimized, setMinimized] = useState(false);
  const { panelRef, headerRef } = useDraggableJournal(visible && lines.length > 0, anchor);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && !minimized) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines, minimized]);

  if (!visible || lines.length === 0) {
    return null;
  }

  const pos: CSSProperties =
    anchor === 'left'
      ? { left: 10, right: 'auto' }
      : { right: 10, left: 'auto' };

  return (
    <div ref={panelRef} style={{ ...panelBase, ...pos }} className="th-near-journal">
      <div ref={headerRef} style={{ ...headerStyle, cursor: 'grab', touchAction: 'none', userSelect: 'none' }}>
        <div style={titleStyle}>{title ?? tr.combatLogTitle}</div>
        <div style={headerActionsStyle}>
          <button
            type="button"
            style={openLogButtonStyle}
            onClick={() => setMinimized((v) => !v)}
            aria-label={minimized ? 'Expand log window' : 'Minimize log window'}
            title={minimized ? 'Expand log window' : 'Minimize log window'}
          >
            {minimized ? '+' : '\u2212'}
          </button>
          {onOpenLog && (
            <button
              type="button"
              style={openLogButtonStyle}
              onClick={onOpenLog}
              aria-label="Open full adventure log"
              title="Open full adventure log"
            >
              {'\u2197'}
            </button>
          )}
        </div>
      </div>
      {!minimized && (
        <div ref={scrollRef} style={{ ...scrollStyle, maxHeight: `${maxVisibleLines * 18}px` }}>
          {lines.map((line, i) => (
            <p key={`${i}-${line.slice(0, 24)}`} style={lineStyle}>
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
